import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Download, Users, ClipboardList, Clock } from "lucide-react";

function ExportCard({ icon: Icon, title, description, path, filename, testid, teachers }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [teacherId, setTeacherId] = useState("all");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (teachers && teacherId !== "all") params.teacher_id = teacherId;
      const res = await api.get(path, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid={testid}>
      <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h2 className="mt-4 font-heading text-lg font-medium text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>

      {teachers && (
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs">Teacher (optional)</Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="h-11" data-testid={`${testid}-teacher`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teachers</SelectItem>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">From (optional)</Label>
          <Input type="date" className="h-11" data-testid={`${testid}-start`} value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To (optional)</Label>
          <Input type="date" className="h-11" data-testid={`${testid}-end`} value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      <Button onClick={download} disabled={busy} className="mt-4 h-11 w-full" data-testid={`${testid}-download`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Download CSV</>}
      </Button>
    </div>
  );
}

export default function Exports() {
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    api.get("/admin/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl" data-testid="exports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">Exports</h1>
      <p className="mt-1 text-muted-foreground">Download school data as CSV files.</p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ExportCard
          icon={Users}
          title="Student data"
          description="All student profiles including guardian contact info. Date range filters by enrollment date."
          path="/admin/export/students.csv"
          filename="albushra-students.csv"
          testid="export-students"
        />
        <ExportCard
          icon={ClipboardList}
          title="Record history"
          description="All daily records school-wide with student name, teacher name, and every field. Date range filters by record date."
          path="/admin/export/records.csv"
          filename="albushra-records.csv"
          testid="export-records"
        />
        <ExportCard
          icon={Clock}
          title="Time entries"
          description="Teacher clock-in/out hours in Mountain Time. Filter by teacher and date range (by clock-in date)."
          path="/admin/export/time-entries.csv"
          filename="albushra-time-entries.csv"
          testid="export-time-entries"
          teachers={teachers}
        />
      </div>
    </div>
  );
}
